import { useState } from "react";

import {
  useSendingProfiles,
  useCreateSendingProfile,
  useUpdateSendingProfile,
  useDeleteSendingProfile,
  useSetSendingProfileCredentials,
} from "../../hooks/useSendingProfiles";
import type { SendingProfile } from "../../api/sendingProfiles";
import { ApiError } from "../../lib/api";

// Single-tenant customer scope — mirrors Groups/List.tsx CUSTOMER_ID.
const CUSTOMER_ID = "00000000-0000-0000-0000-000000000001";

export function SendingProfilesList() {
  const { data, isLoading } = useSendingProfiles();
  const createMut = useCreateSendingProfile();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [credsForId, setCredsForId] = useState<string | null>(null);

  const profiles = data?.items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "var(--font-sans)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>
          Sending profiles
        </h1>
        <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 12 }}>
          · {data?.total ?? profiles.length} profile{(data?.total ?? profiles.length) === 1 ? "" : "s"}
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setShowCreate((v) => !v)}
          style={btnPrimary}
        >
          + New profile
        </button>
      </div>

      <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: "var(--text-muted)", maxWidth: 640 }}>
        BYO-SMTP sender identities for email engagements. The platform sends lures
        through your own SMTP credentials — the username/password are stored in
        Secrets Manager and are never displayed back here.
      </p>

      {showCreate && (
        <ProfileForm
          mode="create"
          pending={createMut.isPending}
          onCancel={() => setShowCreate(false)}
          onSubmit={(body) =>
            createMut.mutate(
              { customer_id: CUSTOMER_ID, ...body },
              { onSuccess: () => setShowCreate(false) },
            )
          }
        />
      )}

      {isLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
      ) : profiles.length === 0 ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px dashed var(--border)",
            borderRadius: 12,
            padding: 24,
            textAlign: "center",
            fontSize: 12,
            color: "var(--text-muted)",
          }}
        >
          No sending profiles yet. Create one above to send email engagements.
        </div>
      ) : (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: 12,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ListHeader />
          {profiles.map((p) => (
            <ProfileRow
              key={p.sending_profile_id}
              profile={p}
              editing={editingId === p.sending_profile_id}
              onEdit={() => setEditingId(p.sending_profile_id)}
              onStopEdit={() => setEditingId(null)}
              onSetCreds={() => setCredsForId(p.sending_profile_id)}
            />
          ))}
        </div>
      )}

      {credsForId && (
        <CredentialsModal
          profileId={credsForId}
          onClose={() => setCredsForId(null)}
        />
      )}
    </div>
  );
}

// ─── Row (read + inline edit) ───────────────────────────────────────────────────

function ProfileRow({
  profile,
  editing,
  onEdit,
  onStopEdit,
  onSetCreds,
}: {
  profile: SendingProfile;
  editing: boolean;
  onEdit: () => void;
  onStopEdit: () => void;
  onSetCreds: () => void;
}) {
  const updateMut = useUpdateSendingProfile(profile.sending_profile_id);
  const deleteMut = useDeleteSendingProfile();

  if (editing) {
    return (
      <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
        <ProfileForm
          mode="edit"
          pending={updateMut.isPending}
          initial={profile}
          onCancel={onStopEdit}
          onSubmit={(body) => updateMut.mutate(body, { onSuccess: onStopEdit })}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.2fr 1.6fr 1.4fr 0.8fr 1.2fr",
        gap: 10,
        padding: "10px 14px",
        borderBottom: "1px solid var(--border-subtle)",
        alignItems: "center",
        fontSize: 11.5,
      }}
    >
      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{profile.name}</span>
      <span style={{ color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {profile.from_display_name} &lt;{profile.from_address}&gt;
      </span>
      <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>
        {profile.smtp_host}:{profile.smtp_port}
        {profile.smtp_use_tls ? " · TLS" : ""}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: profile.has_credentials ? "var(--success)" : "var(--warning)",
        }}
      >
        {profile.has_credentials ? "CREDS SET" : "NO CREDS"}
      </span>
      <span style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <LinkButton onClick={onSetCreds}>Set creds</LinkButton>
        <LinkButton onClick={onEdit}>Edit</LinkButton>
        <LinkButton
          danger
          onClick={() => {
            if (confirm(`Delete sending profile "${profile.name}"?`)) {
              deleteMut.mutate(profile.sending_profile_id);
            }
          }}
        >
          Delete
        </LinkButton>
      </span>
    </div>
  );
}

// ─── Create / edit form ───────────────────────────────────────────────────────────

interface ProfileFormBody {
  name: string;
  from_address: string;
  from_display_name: string;
  smtp_host: string;
  smtp_port: number;
  smtp_use_tls: boolean;
}

function ProfileForm({
  mode,
  initial,
  pending,
  onSubmit,
  onCancel,
}: {
  mode: "create" | "edit";
  initial?: SendingProfile;
  pending: boolean;
  onSubmit: (body: ProfileFormBody) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [fromAddress, setFromAddress] = useState(initial?.from_address ?? "");
  const [fromDisplay, setFromDisplay] = useState(initial?.from_display_name ?? "");
  const [smtpHost, setSmtpHost] = useState(initial?.smtp_host ?? "");
  const [smtpPort, setSmtpPort] = useState(initial?.smtp_port ?? 587);
  const [useTls, setUseTls] = useState(initial?.smtp_use_tls ?? true);

  const valid = name.trim() && fromAddress.trim() && fromDisplay.trim() && smtpHost.trim() && smtpPort > 0;

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: 16,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: 12,
        alignItems: "end",
      }}
    >
      <Field label="Name *">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme SES" style={inputStyle} />
      </Field>
      <Field label="From display name *">
        <input value={fromDisplay} onChange={(e) => setFromDisplay(e.target.value)} placeholder="Acme IT Help Desk" style={inputStyle} />
      </Field>
      <Field label="From address *">
        <input value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} placeholder="helpdesk@acme.example" style={inputStyle} />
      </Field>
      <Field label="SMTP host *">
        <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="email-smtp.us-east-1.amazonaws.com" style={inputStyle} />
      </Field>
      <Field label="SMTP port *">
        <input
          type="number"
          min={1}
          value={smtpPort}
          onChange={(e) => setSmtpPort(parseInt(e.target.value, 10) || 0)}
          style={inputStyle}
        />
      </Field>
      <Field label="TLS">
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-primary)" }}>
          <input type="checkbox" checked={useTls} onChange={(e) => setUseTls(e.target.checked)} />
          Use STARTTLS / TLS
        </label>
      </Field>
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
        <button
          disabled={!valid || pending}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              from_address: fromAddress.trim(),
              from_display_name: fromDisplay.trim(),
              smtp_host: smtpHost.trim(),
              smtp_port: smtpPort,
              smtp_use_tls: useTls,
            })
          }
          style={{ ...btnPrimary, opacity: !valid ? 0.5 : 1, cursor: !valid ? "not-allowed" : "pointer" }}
        >
          {pending ? "Saving…" : mode === "create" ? "Create" : "Save"}
        </button>
        <button onClick={onCancel} style={btnGhost}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Set-credentials modal (write-only) ───────────────────────────────────────────

function CredentialsModal({ profileId, onClose }: { profileId: string; onClose: () => void }) {
  const setCredsMut = useSetSendingProfileCredentials(profileId);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const valid = username.trim() && password.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 22,
          width: "min(440px, 92vw)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18, color: "var(--text-primary)" }}>
          Set SMTP credentials
        </h3>
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
          Stored in Secrets Manager — write-only. These values are never displayed
          again; re-enter to rotate.
        </p>
        <Field label="SMTP username *">
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" style={inputStyle} />
        </Field>
        <Field label="SMTP password *">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" style={inputStyle} />
        </Field>
        {error && (
          <div style={{ fontSize: 11, color: "var(--error)", fontFamily: "var(--font-mono)" }}>{error}</div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button
            disabled={!valid || setCredsMut.isPending}
            onClick={() =>
              setCredsMut.mutate(
                { username: username.trim(), password },
                {
                  onSuccess: onClose,
                  onError: (err) =>
                    setError(err instanceof ApiError ? `${err.status}: ${err.detail}` : "Failed to save credentials."),
                },
              )
            }
            style={{ ...btnPrimary, opacity: !valid ? 0.5 : 1, cursor: !valid ? "not-allowed" : "pointer" }}
          >
            {setCredsMut.isPending ? "Saving…" : "Save credentials"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bits ──────────────────────────────────────────────────────────────────────────

function ListHeader() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.2fr 1.6fr 1.4fr 0.8fr 1.2fr",
        gap: 10,
        padding: "8px 14px",
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: "var(--text-muted)",
        fontWeight: 700,
        background: "var(--bg-secondary)",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <span>Name</span>
      <span>From</span>
      <span>SMTP</span>
      <span>Credentials</span>
      <span style={{ textAlign: "right" }}>Actions</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-muted)" }}>
        {label}
      </span>
      {children}
    </div>
  );
}

function LinkButton({
  children,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        fontSize: 11,
        textDecoration: "underline",
        color: danger ? "var(--error)" : "var(--accent)",
      }}
    >
      {children}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--bg-input)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "7px 10px",
  color: "var(--text-primary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const btnPrimary: React.CSSProperties = {
  background: "var(--accent)",
  color: "#fff",
  border: "1px solid var(--accent)",
  borderRadius: "var(--radius-input)",
  padding: "8px 16px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const btnGhost: React.CSSProperties = {
  background: "transparent",
  color: "var(--text-secondary)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-input)",
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};
